// Thanks to: https://medium.com/geekculture/creating-a-dropdown-with-nested-menu-items-using-react-mui-bb0c084226da

import React, {
  FocusEvent,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  useRef,
  useState,
} from "react";

import ArrowRight from "@mui/icons-material/ArrowRight";
import Menu from "@mui/material/Menu";
import MenuItem, { MenuItemProps } from "@mui/material/MenuItem";
import { SxProps, Theme } from "@mui/material/styles";
import { useForkRef } from "@mui/material/utils";

type ContainerProps = Omit<React.HTMLAttributes<HTMLDivElement>, "ref"> & {
  ref?: React.Ref<HTMLDivElement>;
};

export interface NestedMenuItemProps
  extends Omit<MenuItemProps, "ref" | "tabIndex"> {
  parentMenuOpen?: boolean;
  label: ReactNode;
  rightIcon?: ReactNode;
  customTheme?: SxProps<Theme>;
  ContainerProps?: ContainerProps;
  rightAnchored?: boolean;
  tabIndex?: number;
  keepOpen?: boolean;
  menu?: ReactNode;
}

const NestedMenuItem = React.forwardRef<HTMLLIElement, NestedMenuItemProps>(
  (props, ref) => {
    const {
      parentMenuOpen,
      label,
      rightIcon = <ArrowRight style={{ fontSize: 16 }} />,
      keepOpen: _keepOpen,
      children,
      customTheme,
      className,
      tabIndex: tabIndexProp,
      ContainerProps: ContainerPropsProp,
      rightAnchored,
      ...menuItemProps
    } = props;

    const resolvedContainerProps: ContainerProps = ContainerPropsProp ?? {};
    const {
      ref: containerRefProp,
      onMouseEnter: containerOnMouseEnter,
      onMouseLeave: containerOnMouseLeave,
      onFocus: containerOnFocus,
      ...containerProps
    } = resolvedContainerProps;

    const menuItemRef = useRef<HTMLLIElement | null>(null);
    const handleMenuItemRef = useForkRef(menuItemRef, ref);

    const containerRef = useRef<HTMLDivElement | null>(null);
    const handleContainerRef = useForkRef(containerRef, containerRefProp);

    const menuContainerRef = useRef<HTMLDivElement | null>(null);

    const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);

    const handleMouseEnter = (event: MouseEvent<HTMLDivElement>) => {
      setIsSubMenuOpen(true);

      containerOnMouseEnter?.(event);
    };

    const handleMouseLeave = (event: MouseEvent<HTMLDivElement>) => {
      setIsSubMenuOpen(false);

      containerOnMouseLeave?.(event);
    };

    const isSubmenuFocused = () => {
      const active = containerRef.current?.ownerDocument?.activeElement;

      for (const child of menuContainerRef.current?.children ?? []) {
        if (child === active) {
          return true;
        }
      }
      return false;
    };

    const handleFocus = (event: FocusEvent<HTMLDivElement>) => {
      if (event.target === containerRef.current) {
        setIsSubMenuOpen(true);
      }

      containerOnFocus?.(event);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Escape") {
        return;
      }

      if (isSubmenuFocused()) {
        event.stopPropagation();
      }

      const active = containerRef.current?.ownerDocument?.activeElement;

      if (event.key === "ArrowLeft" && isSubmenuFocused()) {
        containerRef.current?.focus();
      }

      if (
        event.key === "ArrowRight" &&
        event.target === containerRef.current &&
        event.target === active
      ) {
        const firstChild = menuContainerRef.current?.children?.item(
          0,
        ) as HTMLElement | null;
        firstChild?.focus();
      }
    };

    const open = isSubMenuOpen && Boolean(parentMenuOpen);

    let tabIndex: number | undefined;
    if (!props.disabled) {
      tabIndex = tabIndexProp ?? -1;
    }

    return (
      <div
        {...containerProps}
        ref={handleContainerRef}
        role="button"
        tabIndex={tabIndex}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <MenuItem
          {...menuItemProps}
          ref={handleMenuItemRef}
          className={className}
          data-open={open || undefined}
        >
          {label}
          <div style={{ flexGrow: 1 }} />
          {rightIcon}
        </MenuItem>
        <Menu
          disableAutoFocus
          disableEnforceFocus
          hideBackdrop
          anchorEl={menuItemRef.current}
          anchorOrigin={{
            vertical: "top",
            horizontal: rightAnchored ? "left" : "right",
          }}
          open={!!open}
          style={{ pointerEvents: "none" }}
          sx={customTheme}
          transformOrigin={{
            vertical: "top",
            horizontal: rightAnchored ? "right" : "left",
          }}
          onClose={() => {
            setIsSubMenuOpen(false);
          }}
        >
          <div ref={menuContainerRef} style={{ pointerEvents: "auto" }}>
            {children}
          </div>
        </Menu>
      </div>
    );
  },
);

NestedMenuItem.displayName = "NestedMenuItem";

export default NestedMenuItem;
